import type React from "react";
import { useLocation } from "react-router-dom";

// Components
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import FileList from "@/pages/workspace/partials/workspace-tabs/components/files/FileList";
import FileGrid from "@/pages/workspace/partials/workspace-tabs/components/files/FileGrid";
import FilesBreadCrumbs from "@/pages/workspace/partials/workspace-tabs/components/files/FilesBreadCrumbs";
import EmptyState from "@/pages/workspace/partials/workspace-tabs/components/files/EmptyState";
import FilesTabToolbar from "@/pages/workspace/partials/workspace-tabs/components/files/FilesTabToolbar";
import UploadFilesPopoverList from "@/components/upload/UploadFilesPopoverList";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import CreateFolderDialog from "@/pages/workspace/components/CreateFolderDialog";
import { OneDriveSyncDialog } from "@/pages/workspace/components/OneDriveSyncDialog";
import MetadataFormDialog from "@/pages/workspace/partials/workspace-tabs/components/files/metadata-form-dialog/MetadataFormDialog";
import DeletedFilesDialog from "@/components/dialogs/deleted-files-dialog";
import SyncReportDialog from "@/components/dialogs/sync-report-dialog";
import { useDeletedFilesDialogStore } from "@/stores/deleted-files-dialog.store";
import { useSyncReportDialogStore } from "@/stores/sync-report-dialog.store";

// Hooks - Core
import useWorkspace from "@/hooks/useWorkspace";
import useFileSync from "@/pages/workspace/hooks/useFileSync";

// Hooks - FilesTab specific
import { useFilesTabSearch } from "../../hooks/useFilesTabSearch";
import { useFilesTabEffects } from "../../hooks/useFilesTabEffects";

// Context & Stores
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { useFilesTabNavigation } from "../../hooks/useFilesTabNavigation";
import { useDocumentIndexingStatuses } from "../../hooks/useDocumentIndexingStatuses";
import { StaleSearchIndexNotice } from "@/components/indexing-status/StaleSearchIndexNotice";
import {
  StaleIndexDocument,
  useStaleSearchIndex,
} from "@/hooks/useStaleSearchIndex";
import { canReindexItem } from "@/pages/workspace/utils/workspaceAccess";
import { useState, useEffect, useMemo } from "react";
import { useSharePointContext } from "../../providers/SharePointContext";
import { FileOperationsProvider } from "@/pages/workspace/providers/FileOperationsContext";
import { useFileOperations } from "@/pages/workspace/hooks/useFileOperations";
import {
  getAllWorkspaceItems,
  isActiveRevisionSearchIndexStale,
  isItemIndexable,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LABELS,
  ITEM_TYPE_LINK,
  Item,
  sortItemsBy,
} from "@/pages/workspace/utils/workspaceItemUtils";

// Utils & Types
import { useUploadHub } from "../../hooks/useUploadHub";
import {
  ConflictingFile,
  useUploadStateStore,
} from "@/stores/upload-state.store";
import UploadFeedbackDialog from "@/components/upload/UploadFeedbackDialog";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { getAllowedFileExtensions } from "@/utils/allowedFileExtensions";

// Models
import WordInformationDialog from "../../components/WordInformationDialog";
import MoveItemModal from "../../components/MoveItemModal";
import RenameFolderDialog from "../../components/RenameFolderDialog";
import RenameDocumentDialog from "../../components/RenameDocumentDialog";
import {
  CLOUD_RENAME_MESSAGES,
  renameCloudDocument,
} from "@/pages/workspace/lib/graphRename";
import {
  CLOUD_STILL_RENAMED_MESSAGE,
  runDocumentRename,
  WORKSPACE_RENAME_FAILED_MESSAGE,
} from "@/pages/workspace/lib/renameDocumentFlow";
import LinkDialog from "../../components/LinkDialog";

/**
 * Inner content component that uses the FileOperationsContext
 */
const FilesTabContent: React.FC<{
  sortedItems: Item[];
  groupedSearchResults: { [path: string]: Item[] };
  shouldGroupSearchResults: boolean;
  selectedItems: string[];
  isNavigating: boolean;
  currentPath: Array<{ id: string; name: string }>;
  currentItems: Item[];
  navigateToPath: (index: number) => void;
  viewType: "list" | "grid";
  setViewType: React.Dispatch<React.SetStateAction<"list" | "grid">>;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  isCreateFolderDialogOpen: boolean;
  setIsCreateFolderDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAddLinkDialogOpen: boolean;
  setIsAddLinkDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  sortedItems,
  groupedSearchResults,
  shouldGroupSearchResults,
  selectedItems,
  isNavigating,
  currentPath,
  currentItems,
  navigateToPath,
  viewType,
  setViewType,
  sortBy,
  setSortBy,
  isCreateFolderDialogOpen,
  setIsCreateFolderDialogOpen,
  isAddLinkDialogOpen,
  setIsAddLinkDialogOpen,
}) => {
  const {
    workspace,
    canUserUploadFiles,
    myUser,
    isParentWorkspace,
    isOrphanedInspection,
  } = useWorkspaceContext();
  const {
    openEditDocumentDialog,
    setOpenEditDocumentDialog,
    wordInformationDialogOpen,
    setWordInformationDialogOpen,
    wordInformationItem,
    isSyncingToSharePoint,
    setIsSyncingToSharePoint,
    syncDocument,
    moveModalItem,
    setMoveModalItem,
    renameFolderItem,
    setRenameFolderItem,
    renameDocumentItem,
    setRenameDocumentItem,
    editLinkItem,
    setEditLinkItem,
    syncPathAfterFolderRename,
    deleteDialogItem,
    isDeleteDialogOpen,
    isDeleteDialogPending,
    closeDeleteDialog,
    confirmDeleteDialog,
  } = useFileOperations();

  const { selectedFolder, openCloudLocationChooser } = useSharePointContext();
  const {
    addFolder,
    updateFolder,
    renameDocument,
    moveFolder,
    moveDocuments,
    addLink,
    updateLink,
    moveLink,
  } = useWorkspace();
  const {
    handleInitialSharePointSync,
    handleDeleteSelectedFiles,
    handleDeleteMissingDocuments,
  } = useFileSync();

  // Upload state from store
  const {
    isUploadMetadataDialogOpen,
    pendingMetadataFiles,
    conflictingFiles,
    conflictingFilesWithPath,
    isConflictDialogOpen,
    pendingOneDriveFiles,
  } = useUploadStateStore();

  const {
    uploads,
    fileInputRef,
    folderInputRef,
    onFileInputChange,
    onFolderInputChange,
    setUploads,
    onMetadataCancel,
    onMetadataConfirm,
    onOverwriteFilesCancel,
    onOverwriteFilesConfirm,
  } = useUploadHub();

  // Deleted files dialog state from Zustand store
  const {
    isOpen: deletedFilesDialogOpen,
    setIsOpen: setDeletedFilesDialogOpen,
    deletedFiles,
    isDeleting: isDeletingFiles,
  } = useDeletedFilesDialogStore();

  // Sync report dialog state
  const {
    isOpen: syncReportDialogOpen,
    results: syncReportResults,
    closeDialog: closeSyncReportDialog,
  } = useSyncReportDialogStore();

  const [showFeedback, setShowFeedback] = useState(false);

  const feedbackUploads = useMemo(
    () =>
      uploads.filter(
        (upload) =>
          upload.feedback &&
          Object.keys(upload.feedback).length > 0 &&
          !upload.feedbackSeen
      ),
    [uploads]
  );

  // Auto-open feedback dialog only when there is new, non-empty feedback
  // and the sync report is not already showing it inline.
  useEffect(() => {
    if (feedbackUploads.length > 0 && !showFeedback && !syncReportDialogOpen) {
      setShowFeedback(true);
    }
  }, [feedbackUploads, showFeedback, syncReportDialogOpen]);

  // Derived state
  const { indexingDocuments } = useDocumentIndexingStatuses(
    workspace?.id,
    sortedItems
  );
  // The stale-index notice and the Status sort only need to know which documents are running.
  const indexingDocumentIds = useMemo(
    () => new Set(indexingDocuments.keys()),
    [indexingDocuments]
  );

  // The Status sort ranks running documents on their own, and only this
  // component knows which are running, so it re-sorts what the search hook
  // sorted. The indexing hook above reads the items, not their order.
  const sortByStatusWithRuns =
    sortBy === "isIndexed" && indexingDocumentIds.size > 0;
  const displayItems = useMemo(
    () =>
      sortByStatusWithRuns
        ? [...sortedItems].sort((a, b) =>
            sortItemsBy(a, b, sortBy, indexingDocumentIds)
          )
        : sortedItems,
    [sortByStatusWithRuns, sortedItems, sortBy, indexingDocumentIds]
  );
  const displayGroups = useMemo(
    () =>
      sortByStatusWithRuns
        ? Object.fromEntries(
            Object.entries(groupedSearchResults).map(([path, items]) => [
              path,
              [...items].sort((a, b) =>
                sortItemsBy(a, b, sortBy, indexingDocumentIds)
              ),
            ])
          )
        : groupedSearchResults,
    [sortByStatusWithRuns, groupedSearchResults, sortBy, indexingDocumentIds]
  );

  // Documents anywhere in the workspace whose search index predates the current pipeline
  // version (LAR-235). The notice above the list explains the amber markers and offers one
  // rerun for all of them; the files tab's own poller above tracks the runs it starts.
  const staleIndexDocuments = useMemo<StaleIndexDocument[]>(() => {
    if (!workspace) return [];
    const canReindex =
      !!myUser &&
      !isOrphanedInspection &&
      canReindexItem(workspace, myUser.id, isParentWorkspace);
    return getAllWorkspaceItems(workspace)
      .filter(
        (item) =>
          item.type === ITEM_TYPE_DOCUMENT &&
          isItemIndexable(item) &&
          isActiveRevisionSearchIndexStale(item)
      )
      .map((item) => ({
        id: item.id,
        workspaceId: workspace.id,
        searchIndexStale: true,
        canReindex,
      }));
  }, [workspace, myUser, isOrphanedInspection, isParentWorkspace]);
  const staleIndex = useStaleSearchIndex(staleIndexDocuments, {
    trackRuns: false,
    indexingDocumentIds,
  });

  const isEmptyState = sortedItems.length === 0;
  const deleteItemTypeLabel = deleteDialogItem
    ? ITEM_TYPE_LABELS[deleteDialogItem.type].indefinite
    : "element";
  return (
    <>
      {!workspace ? (
        <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      ) : (
        <div className="space-y-2 pb-3">
          <div className="sticky top-0 z-20 bg-background pt-2 pb-3 -mx-4 xl:-mx-8 px-4 xl:px-8 border-b">
            <FilesTabToolbar
              sortBy={sortBy}
              setSortBy={setSortBy}
              viewType={viewType}
              setViewType={setViewType}
              setIsCreateFolderDialogOpen={setIsCreateFolderDialogOpen}
              setIsAddLinkDialogOpen={setIsAddLinkDialogOpen}
            />

            <div className="mt-2">
              <FilesBreadCrumbs
                currentPath={currentPath}
                navigateToPath={navigateToPath}
              />
            </div>
          </div>
          {staleIndex.staleCount > 0 && (
            <StaleSearchIndexNotice
              scope="workspace"
              staleCount={staleIndex.staleCount}
              reindexableCount={staleIndex.reindexableCount}
              runningCount={staleIndex.runningCount}
              batchSize={staleIndex.batchSize}
              onReindexAll={staleIndex.reindexAll}
            />
          )}
          {isEmptyState && (
            <EmptyState
              currentPath={currentPath}
              canUserUploadFiles={canUserUploadFiles}
              setIsCreateFolderDialogOpen={setIsCreateFolderDialogOpen}
              setIsAddLinkDialogOpen={setIsAddLinkDialogOpen}
            />
          )}

          {isNavigating && (
            <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          )}

          {!isEmptyState &&
            !isNavigating &&
            (viewType === "list" || shouldGroupSearchResults) && (
              <FileList
                sortedItems={displayItems}
                groupedSearchResults={displayGroups}
                shouldGroupSearchResults={shouldGroupSearchResults || false}
                indexingDocuments={indexingDocuments}
              />
            )}

          {!isEmptyState &&
            !isNavigating &&
            viewType === "grid" &&
            !shouldGroupSearchResults && (
              <FileGrid
                sortedItems={displayItems}
                selectedItems={selectedItems}
                indexingDocuments={indexingDocuments}
              />
            )}
          <UploadFilesPopoverList
            key={uploads.length}
            uploadItems={uploads}
            onClose={() => {
              setUploads([]);
            }}
          />
          <CreateFolderDialog
            isOpen={isCreateFolderDialogOpen}
            setIsOpen={setIsCreateFolderDialogOpen}
            onCreateFolder={async (name) => {
              try {
                await addFolder.mutateAsync({
                  workspaceId: workspace.id,
                  requestBody: {
                    name,
                    parentFolderId:
                      currentPath.length > 0
                        ? currentPath[currentPath.length - 1].id
                        : undefined,
                  },
                });
              } catch (error) {
                logger.error("Failed to create workspace folder", {
                  operation: "createWorkspaceFolder",
                  error,
                  workspaceId: workspace.id,
                  parentFolderId:
                    currentPath.length > 0
                      ? currentPath[currentPath.length - 1].id
                      : undefined,
                });
                showErrorToast(
                  "Fejl ved oprettelse af mappe. Prøv igen senere."
                );
              } finally {
                setIsCreateFolderDialogOpen(false);
              }
            }}
            isPending={addFolder.isPending}
          />
          <LinkDialog
            isOpen={isAddLinkDialogOpen || !!editLinkItem}
            onClose={() => {
              setIsAddLinkDialogOpen(false);
              setEditLinkItem(null);
            }}
            item={editLinkItem}
            isPending={addLink.isPending || updateLink.isPending}
            onSubmit={async ({ name, url }) => {
              const folderId =
                currentPath.length > 0
                  ? currentPath[currentPath.length - 1].id
                  : undefined;
              try {
                if (editLinkItem) {
                  await updateLink.mutateAsync({
                    workspaceId: workspace.id,
                    linkId: editLinkItem.id,
                    requestBody: { name, url },
                  });
                  showSuccessToast("Linket er opdateret");
                } else {
                  await addLink.mutateAsync({
                    workspaceId: workspace.id,
                    requestBody: { name, url, folderId },
                  });
                  showSuccessToast("Linket er tilføjet");
                }
              } catch (error) {
                logger.error("Failed to save workspace link", {
                  operation: editLinkItem
                    ? "updateWorkspaceLink"
                    : "addWorkspaceLink",
                  error,
                  workspaceId: workspace.id,
                  linkId: editLinkItem?.id,
                  folderId,
                });
                showErrorToast(
                  editLinkItem
                    ? "Kunne ikke gemme linket. Tjek webadressen og prøv igen."
                    : "Kunne ikke tilføje linket. Tjek webadressen og prøv igen."
                );
                throw error;
              }
            }}
          />
          <UploadFeedbackDialog
            uploads={feedbackUploads}
            onClose={() => {
              if (feedbackUploads.length > 0) {
                setUploads((prevUploads) =>
                  prevUploads.map((upload) =>
                    feedbackUploads.some((fu) => fu.id === upload.id)
                      ? { ...upload, feedbackSeen: true }
                      : upload
                  )
                );
              }
              setShowFeedback(false);
            }}
            isOpen={showFeedback && feedbackUploads.length > 0}
          />
          {isSyncingToSharePoint && syncDocument && (
            <OneDriveSyncDialog
              isOpen={isSyncingToSharePoint}
              onClose={() => {
                setIsSyncingToSharePoint(false);
              }}
              onSync={async () => {
                await handleInitialSharePointSync(syncDocument);
              }}
              fileName={syncDocument?.name}
              onChooseFolder={(e: React.MouseEvent<HTMLButtonElement>) => {
                openCloudLocationChooser(e, "folder", "selection");
              }}
              path={selectedFolder?.name}
            />
          )}
          <ConfirmationDialog
            isOpen={isConflictDialogOpen}
            onClose={onOverwriteFilesCancel}
            onConfirm={onOverwriteFilesConfirm}
            title={
              pendingOneDriveFiles.length > 0
                ? "OneDrive fil med samme navn fundet"
                : "Fil med samme navn fundet"
            }
            description={(() => {
              // Use conflictingFilesWithPath if available (folder uploads), otherwise fall back to conflictingFiles
              const filesToShow: ConflictingFile[] =
                conflictingFilesWithPath.length > 0
                  ? conflictingFilesWithPath
                  : Array.isArray(conflictingFiles) &&
                      conflictingFiles.length > 0 &&
                      typeof conflictingFiles[0] === "object" &&
                      "file" in conflictingFiles[0]
                    ? (conflictingFiles as ConflictingFile[])
                    : Array.isArray(conflictingFiles) &&
                        conflictingFiles.length > 0
                      ? (conflictingFiles as File[]).map((file) => ({
                          file,
                          folderPath: "denne mappe",
                        }))
                      : [];

              if (filesToShow.length === 0) {
                return "Der findes allerede filer med samme navne.";
              }

              const hasMultipleFiles = filesToShow.length > 1;

              if (hasMultipleFiles) {
                return `Der findes allerede ${filesToShow.length} filer med følgende navne:\n\n${filesToShow
                  .map(
                    (cf) =>
                      `• ${cf.file.name}${
                        cf.folderPath && cf.folderPath !== "denne mappe"
                          ? ` (i mappen: ${cf.folderPath})`
                          : ""
                      }`
                  )
                  .join(
                    "\n"
                  )}\n\nVil du overskrive disse filer?\n\nVed overskrivning bliver de uploadede filer tilføjet som nye versioner.\nDe eksisterende bliver ikke slettet, men vil kunne fremfindes som ældre versioner.\n\nHvis du ikke vil overskive de eksisterende filer, men gerne vil uploade de nye filer, skal du tilføje dem til en ny mappe eller ændre navnet på de filer, du gerne vil uploade.`;
              } else {
                const firstFile = filesToShow[0];
                if (!firstFile) {
                  return "Der findes allerede en fil med samme navn.";
                }
                const pathText =
                  firstFile.folderPath && firstFile.folderPath !== "denne mappe"
                    ? ` i mappen: ${firstFile.folderPath}`
                    : " i denne mappe";
                return `Der findes allerede en fil med dette navn${pathText}:\n\n• ${firstFile.file.name}\n\nVil du overskrive denne fil?\n\nVed overskrivning bliver den uploadede fil tilføjet som en ny version, og metadataen vil blive overskrevet med den nye fils metadata.\nDen eksisterende bliver ikke slettet, men vil kunne fremfindes som ældre version, sammen med dens metadata.\n\nHvis du ikke vil overskive den eksisterende fil, men gerne vil uploade den nye fil, skal du tilføje den til en ny mappe eller ændre navnet på filen, du gerne vil uploade.`;
              }
            })()}
            confirmText="Overskriv filer"
            variant="destructive"
            attentionText={
              pendingOneDriveFiles.length > 0
                ? "Advarsel: Dette vil overskrive de eksisterende filer og tilføje en ny version fra OneDrive."
                : "Advarsel: Dette vil overskrive de eksisterende filer og tilføje en ny version."
            }
          />
          <ConfirmationDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDeleteDialog}
            onConfirm={confirmDeleteDialog}
            confirmPending={isDeleteDialogPending}
            title={`Slet ${deleteItemTypeLabel}`}
            description={`Er du sikker på, at du vil slette ${deleteDialogItem?.name ?? ""}?\nDenne handling kan ikke fortrydes.`}
            confirmText={`Slet ${deleteItemTypeLabel}`}
            variant="destructive"
          />
          <WordInformationDialog
            wordInformationItem={wordInformationItem}
            isOpen={wordInformationDialogOpen}
            onClose={() => {
              setWordInformationDialogOpen(false);
            }}
          />
          <DeletedFilesDialog
            isOpen={deletedFilesDialogOpen}
            onClose={() => setDeletedFilesDialogOpen(false)}
            deletedFiles={deletedFiles}
            onDelete={handleDeleteSelectedFiles}
            isDeleting={isDeletingFiles}
          />
          {syncReportDialogOpen && syncReportResults && (
            <SyncReportDialog
              isOpen={syncReportDialogOpen}
              onClose={() => {
                // Mark any upload feedback shown inline as seen so the
                // standalone UploadFeedbackDialog doesn't re-open afterwards.
                if (feedbackUploads.length > 0) {
                  setUploads((prev) =>
                    prev.map((u) =>
                      feedbackUploads.some((fu) => fu.id === u.id)
                        ? { ...u, feedbackSeen: true }
                        : u
                    )
                  );
                }
                closeSyncReportDialog();
              }}
              results={syncReportResults}
              onDelete={handleDeleteMissingDocuments}
              uploadFeedback={feedbackUploads}
            />
          )}
          <MetadataFormDialog
            editDocument={openEditDocumentDialog}
            onClose={() => {
              if (openEditDocumentDialog) {
                setOpenEditDocumentDialog(null);
              } else if (isUploadMetadataDialogOpen) {
                onMetadataCancel();
              }
            }}
            workspaceId={workspace.id}
            isUploadMode={isUploadMetadataDialogOpen}
            uploadFiles={
              isUploadMetadataDialogOpen
                ? pendingMetadataFiles || undefined
                : undefined
            }
            onUploadConfirm={
              isUploadMetadataDialogOpen ? onMetadataConfirm : undefined
            }
          />
          <MoveItemModal
            isOpen={!!moveModalItem}
            onClose={() => setMoveModalItem(null)}
            workspace={workspace}
            item={moveModalItem}
            isPending={
              moveFolder.isPending ||
              moveDocuments.isPending ||
              moveLink.isPending
            }
            onMove={async ({ targetFolderId, itemType, itemId }) => {
              try {
                if (itemType === ITEM_TYPE_FOLDER) {
                  await moveFolder.mutateAsync({
                    workspaceId: workspace.id,
                    requestBody: {
                      folderId: itemId,
                      newFolderLocation: targetFolderId ?? undefined,
                    },
                  });
                } else if (itemType === ITEM_TYPE_LINK) {
                  await moveLink.mutateAsync({
                    workspaceId: workspace.id,
                    linkId: itemId,
                    requestBody: { newFolderId: targetFolderId ?? undefined },
                  });
                } else {
                  await moveDocuments.mutateAsync({
                    workspaceId: workspace.id,
                    requestBody: {
                      newFolderId: targetFolderId ?? undefined,
                      documentIds: [itemId],
                    },
                  });
                }
              } catch (error) {
                logger.error("Failed to move workspace item", {
                  operation: "moveWorkspaceItem",
                  error,
                  workspaceId: workspace.id,
                  itemId,
                  itemType,
                  targetFolderId,
                });
                showErrorToast(
                  itemType === ITEM_TYPE_LINK
                    ? "Kunne ikke flytte linket. Prøv igen."
                    : "Fejl under flytning. Tjek venligst at mappen ikke allerede har et element med samme navn."
                );
                throw error;
              }
            }}
          />
          <RenameFolderDialog
            isOpen={!!renameFolderItem}
            onClose={() => setRenameFolderItem(null)}
            workspace={workspace}
            item={renameFolderItem}
            isPending={updateFolder.isPending}
            onRename={async (name) => {
              if (!renameFolderItem) return;
              try {
                await updateFolder.mutateAsync({
                  workspaceId: workspace.id,
                  requestBody: {
                    folderId: renameFolderItem.id,
                    folderName: name,
                  },
                });
                showSuccessToast("Mappen er omdøbt");
                syncPathAfterFolderRename(renameFolderItem.id, name);
              } catch (error) {
                logger.error("Failed to rename workspace folder", {
                  operation: "renameWorkspaceFolder",
                  error,
                  workspaceId: workspace.id,
                  folderId: renameFolderItem.id,
                });
                showErrorToast(
                  "Kunne ikke omdøbe mappen. Tjek venligst at der ikke allerede findes en mappe med samme navn."
                );
                throw error;
              }
            }}
          />
          <RenameDocumentDialog
            isOpen={!!renameDocumentItem}
            onClose={() => setRenameDocumentItem(null)}
            workspace={workspace}
            item={renameDocumentItem}
            isPending={renameDocument.isPending}
            onRename={async (name) => {
              if (!renameDocumentItem) return;
              const item = renameDocumentItem;
              const logContext = {
                operation: "renameWorkspaceDocument",
                workspaceId: workspace.id,
                documentId: item.id,
              };

              // Cloud first, then the workspace, with the cloud rename undone if
              // the workspace refuses — see runDocumentRename for why that order.
              const outcome = await runDocumentRename(item, name, {
                renameInCloud: renameCloudDocument,
                renameInWorkspace: (documentName) =>
                  renameDocument.mutateAsync({
                    workspaceId: workspace.id,
                    documentId: item.id,
                    requestBody: { documentName },
                  }),
              });

              if (outcome.ok) {
                showSuccessToast(
                  outcome.renamedInCloud
                    ? "Filen er omdøbt her og i OneDrive/SharePoint"
                    : "Filen er omdøbt"
                );
                return;
              }

              if (outcome.stage === "cloud") {
                logger.error(
                  "Failed to rename cloud-linked document in OneDrive/SharePoint",
                  {
                    ...logContext,
                    reason: outcome.reason,
                    status: outcome.status,
                    graphErrorCode: outcome.graphErrorCode,
                  }
                );
                showErrorToast(CLOUD_RENAME_MESSAGES[outcome.reason]);
                throw new Error(`Cloud rename failed: ${outcome.reason}`);
              }

              logger.error("Failed to rename workspace document", {
                ...logContext,
                cloud: outcome.cloud,
                error: outcome.error,
              });
              // Not the API's own message: feedbackFromError falls back to
              // HttpFeedbackMap, whose per-status text is English. A sibling clash
              // from a stale client is the likely cause, so the message names it.
              showErrorToast(
                outcome.cloud === "stillRenamed"
                  ? CLOUD_STILL_RENAMED_MESSAGE
                  : WORKSPACE_RENAME_FAILED_MESSAGE
              );
              throw outcome.error instanceof Error
                ? outcome.error
                : new Error("Workspace rename failed");
            }}
          />
        </div>
      )}
      <input
        type="file"
        accept={getAllowedFileExtensions()}
        ref={fileInputRef}
        className="absolute w-full h-full hidden"
        onChange={(e) => {
          if (canUserUploadFiles() && e.target.files) {
            onFileInputChange(
              e.target.files,
              currentItems,
              currentPath[currentPath.length - 1]?.id
            );
          }
        }}
        multiple
      />
      <input
        type="file"
        ref={(ref) => {
          if (ref) {
            folderInputRef.current = ref;
            ref.setAttribute("webkitdirectory", "");
          }
        }}
        className="absolute w-full h-full hidden"
        onChange={(e) => {
          if (canUserUploadFiles() && e.target.files) {
            onFolderInputChange(
              e.target.files,
              workspace!,
              currentPath[currentPath.length - 1]?.id
            );
            e.target.value = "";
          }
        }}
        multiple
      />
    </>
  );
};

/**
 * Main FilesTab component that sets up the FileOperationsProvider
 * and renders the FilesTabContent.
 */
const FilesTab: React.FC = () => {
  const {
    workspace,
    search,
    filteredWorkspaceItems,
    textMatchIds,
    selectedMetadataFilters,
    setSearch,
  } = useWorkspaceContext();

  const workspaceId = workspace!.id;
  const location = useLocation();
  const openFolderId = location.state?.openFolderId;

  // Local state
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [groupedSearchResults, setGroupedSearchResults] = useState<{
    [path: string]: Item[];
  }>({});
  const [viewType, setViewType] = useState<"list" | "grid">("list");
  const [sortBy, setSortBy] = useState("name");
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [isAddLinkDialogOpen, setIsAddLinkDialogOpen] = useState(false);

  // Navigation
  const { currentPath, currentItems, isNavigating, navigateToPath } =
    useFilesTabNavigation({
      workspace,
      workspaceId,
      openFolderId,
      setSelectedItems,
    });

  // Search and filtering
  const { shouldGroupSearchResults, sortedItems } = useFilesTabSearch({
    currentItems,
    search,
    workspace,
    filteredWorkspaceItems,
    textMatchIds,
    selectedMetadataFilters,
    sortBy,
    setGroupedSearchResults,
  });

  // Effects
  useFilesTabEffects({
    setSearch,
  });

  if (!workspace) {
    return (
      <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    );
  }

  return (
    <FileOperationsProvider
      currentItems={currentItems}
      currentPath={currentPath}
      openFolderId={openFolderId}
      setSelectedItems={setSelectedItems}
    >
      <FilesTabContent
        sortedItems={sortedItems}
        groupedSearchResults={groupedSearchResults}
        shouldGroupSearchResults={shouldGroupSearchResults || false}
        selectedItems={selectedItems}
        isNavigating={isNavigating}
        currentPath={currentPath}
        currentItems={currentItems}
        navigateToPath={navigateToPath}
        viewType={viewType}
        setViewType={setViewType}
        sortBy={sortBy}
        setSortBy={setSortBy}
        isCreateFolderDialogOpen={isCreateFolderDialogOpen}
        setIsCreateFolderDialogOpen={setIsCreateFolderDialogOpen}
        isAddLinkDialogOpen={isAddLinkDialogOpen}
        setIsAddLinkDialogOpen={setIsAddLinkDialogOpen}
      />
    </FileOperationsProvider>
  );
};

export default FilesTab;
